// ============================================================
// excelAvaliacao.js — a planilha da avaliação de encerramento do ciclo
//
// Mesmo padrão do relatório de frequência (excel.js): cabeçalho verde com
// texto branco, núcleo pelo NOME de cadastro e nunca pela chave, colunas
// ajustadas ao conteúdo e nome de arquivo pelo nucleoParaArquivo.
//
// Três abas, porque são três leituras diferentes e empilhá-las numa só
// obriga quem abre a planilha a rolar de lado procurando o que quer:
//
//   Resumo    — a contagem por opção de cada pergunta fechada. É a aba da
//               reunião, e por isso é a primeira que o Excel abre.
//   Respostas — uma linha por atleta, uma coluna por pergunta. É a aba de
//               quem vai cruzar com frequência, idade ou modalidade.
//   Escritas  — as respostas por extenso, uma embaixo da outra. Texto
//               longo em célula de planilha larga não se lê; aqui cada
//               uma fica numa linha própria, com quebra de texto.
//
// A contagem NÃO é refeita aqui: vem de apurar(), a mesma função que
// alimenta a tela e o PDF. Quando cada saída contava do seu jeito, bastava
// um arredondamento diferente para a planilha e o painel discordarem na
// frente da coordenação.
// ============================================================

import ExcelJS from "exceljs";
import { nucleoParaArquivo } from "./nomeDeArquivo";
import { PERGUNTAS, SECOES, CICLO_ROTULO, apurar } from "./avaliacaoCiclo";

const VERDE_EXCEL = "FF00853B";
const BRANCO_EXCEL = "FFFFFFFF";
const CINZA_FUNDO = "FFF9FAFB";

function estilizarHeader(ws) {
  const row = ws.getRow(1);
  row.eachCell(cell => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: VERDE_EXCEL } };
    cell.font = { bold: true, color: { argb: BRANCO_EXCEL }, size: 11 };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  });
  row.height = 32;
}

function ajustarColunas(ws, maximo = 40) {
  ws.columns.forEach(col => {
    let max = 10;
    col.eachCell({ includeEmpty: false }, cell => {
      const v = cell.value ? String(cell.value) : "";
      if (v.length > max) max = v.length;
    });
    col.width = Math.min(max + 4, maximo);
  });
}

// Faixa de seção dentro da aba, igual à do relatório de frequência.
function faixaDeSecao(ws, linha, texto, colunas) {
  ws.mergeCells(linha, 1, linha, colunas);
  const c = ws.getCell(linha, 1);
  c.value = `  ${texto}`;
  c.font = { bold: true, size: 11, color: { argb: BRANCO_EXCEL } };
  c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF374151" } };
  c.alignment = { vertical: "middle" };
  ws.getRow(linha).height = 22;
}

function tituloDaSecao(id) {
  const s = SECOES.find(x => x.id === id);
  return s ? s.titulo : id;
}

// "05/10/2026 14:32" a partir do Timestamp do Firestore ou de uma Date.
function dataHoraBR(valor) {
  if (!valor) return "";
  const d = typeof valor.toDate === "function" ? valor.toDate() : new Date(valor);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export async function gerarExcelAvaliacao({
  avaliacoes, nucleo, nucleoNome, ciclo = CICLO_ROTULO, atletasAtivos = null,
}) {
  const lista = Array.isArray(avaliacoes) ? avaliacoes : [];
  const { fechadas, abertas } = apurar(lista);
  const rotulo = nucleoNome || nucleo || "";

  const wb = new ExcelJS.Workbook();
  wb.creator = "Revelar Talentos";
  wb.calcProperties = wb.calcProperties || {};
  wb.calcProperties.fullCalcOnLoad = true;

  // ── Aba 1: Resumo ─────────────────────────────────────────
  const wsResumo = wb.addWorksheet("Resumo");
  wsResumo.addRow(["Pergunta", "Resposta", "Atletas", "% de quem respondeu"]);
  estilizarHeader(wsResumo);

  let L = 2;
  SECOES.forEach(secao => {
    const daSecao = fechadas.filter(f => f.secao === secao.id);
    if (daSecao.length === 0) return;
    faixaDeSecao(wsResumo, L, tituloDaSecao(secao.id).toUpperCase(), 4);
    L++;
    daSecao.forEach(f => {
      f.opcoes.forEach((o, i) => {
        const row = wsResumo.addRow([
          i === 0 ? f.texto : "",
          o.opcao,
          o.qtd,
          f.responderam > 0 ? o.qtd / f.responderam : 0,
        ]);
        row.getCell(4).numFmt = "0%";
        row.getCell(1).alignment = { wrapText: true, vertical: "top" };
        row.getCell(3).alignment = { horizontal: "center" };
        row.getCell(4).alignment = { horizontal: "center" };
        // A opção que não é juízo e sim ausência de experiência fica em
        // cinza: ela não entra na leitura de "foi bem ou foi mal".
        if (o.neutra) {
          row.getCell(2).font = { italic: true, color: { argb: "FF6B7280" } };
        }
        if (i === 0) row.getCell(1).font = { bold: true };
        L++;
      });
      // Linha em branco entre perguntas: sem ela, trinta e tantas linhas
      // viram um bloco e ninguém acha onde uma pergunta termina.
      wsResumo.addRow([]);
      L++;
    });
  });

  wsResumo.getColumn(1).width = 62;
  wsResumo.getColumn(2).width = 26;
  wsResumo.getColumn(3).width = 10;
  wsResumo.getColumn(4).width = 20;

  // Rodapé do resumo: quem respondeu, de quantos, e o prazo.
  const noPrazo = lista.filter(a => a.noPrazo === true).length;
  L += 1;
  faixaDeSecao(wsResumo, L, "RESUMO", 4);
  L++;
  wsResumo.mergeCells(L, 1, L, 4);
  const rodape = wsResumo.getCell(L, 1);
  rodape.value =
    `Ciclo ${ciclo} · Núcleo ${rotulo}  ·  ${lista.length} avaliação(ões) respondida(s)` +
    (atletasAtivos ? ` de ${atletasAtivos} atleta(s) ativo(s)` : "") +
    `  ·  ${noPrazo} no prazo  ·  ${lista.length - noPrazo} fora do prazo`;
  rodape.font = { size: 11, color: { argb: "FF1A1A1A" } };
  rodape.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CINZA_FUNDO } };
  rodape.alignment = { wrapText: true, vertical: "middle", indent: 1 };
  wsResumo.getRow(L).height = 30;

  // ── Aba 2: Respostas ──────────────────────────────────────
  const wsResp = wb.addWorksheet("Respostas");
  const cabecalho = [
    "Atleta", "Modalidade", "Núcleo", "Idade", "Enviado em", "No prazo",
    ...PERGUNTAS.map(p => p.texto),
  ];
  wsResp.addRow(cabecalho);
  estilizarHeader(wsResp);

  lista.forEach((a, i) => {
    const r = a.respostas || {};
    const row = wsResp.addRow([
      a.nome || "",
      a.modalidade || "",
      rotulo,
      typeof a.idadeNaResposta === "number" ? a.idadeNaResposta : "",
      dataHoraBR(a.enviadoEm),
      a.noPrazo === true ? "Sim" : "Não",
      ...PERGUNTAS.map(p => r[p.id] || ""),
    ]);
    if (i % 2 === 1) {
      row.eachCell(cell => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CINZA_FUNDO } };
      });
    }
    // Fora do prazo em vermelho: é a coluna que a coordenação procura
    // quando vai cobrar quem entregou atrasado.
    if (a.noPrazo !== true) {
      row.getCell(6).font = { bold: true, color: { argb: "FFDC2626" } };
    }
    row.alignment = { vertical: "top" };
  });

  ajustarColunas(wsResp, 46);
  wsResp.getColumn(1).width = 30;
  wsResp.views = [{ state: "frozen", xSplit: 1, ySplit: 1 }];

  // ── Aba 3: Escritas ───────────────────────────────────────
  const wsTexto = wb.addWorksheet("Escritas");
  wsTexto.addRow(["Pergunta", "Atleta", "Modalidade", "Resposta"]);
  estilizarHeader(wsTexto);

  let T = 2;
  abertas.forEach(ab => {
    faixaDeSecao(wsTexto, T, ab.texto.toUpperCase(), 4);
    T++;
    if (ab.respostas.length === 0) {
      const row = wsTexto.addRow(["", "", "", "Ninguém respondeu esta pergunta."]);
      row.getCell(4).font = { italic: true, color: { argb: "FF6B7280" } };
      T++;
      return;
    }
    ab.respostas.forEach(r => {
      const row = wsTexto.addRow(["", r.nome, r.modalidade, r.texto]);
      row.getCell(4).alignment = { wrapText: true, vertical: "top" };
      row.height = 30;
      T++;
    });
  });

  wsTexto.getColumn(1).width = 4;
  wsTexto.getColumn(2).width = 30;
  wsTexto.getColumn(3).width = 18;
  wsTexto.getColumn(4).width = 90;

  // Um aviso, e não um silêncio, quando ninguém respondeu ainda: planilha
  // vazia parece erro de exportação.
  if (lista.length === 0) {
    wsResumo.getCell(2, 1).value = "Nenhuma avaliação respondida neste ciclo até agora.";
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download =
    `Avaliacao_Ciclo_${String(ciclo).replace(/\//g, "-")}_${nucleoParaArquivo(nucleoNome, nucleo)}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
  return link.download;
}

// As colunas que vêm antes das perguntas na aba "Respostas" (atleta,
// modalidade, núcleo, idade, enviado em, no prazo). Exportado para o teste
// conferir que depois delas vem uma coluna por pergunta, nem uma a mais.
export const COLUNAS_FIXAS = 6;
